import type { SummaryView } from '../lib/reports';
import { formatDate, formatLitres, formatRs } from '../lib/money';
import { DocHeader } from '../components/PrintPreview';

/**
 * Cost summary and plant cost report, A4 — both for a chosen period.
 *
 * Neither carries a stamp: they are management reports over a range, not a
 * document that is approved. The day report is where approval lives.
 */

interface Props {
  view: SummaryView;
  printedBy: string;
  printedAt: Date;
}

export default function CostSummaryReport({ view, printedBy, printedAt }: Props) {
  return (
    <>
      <DocHeader
        doc="Cost summary"
        title={`${formatDate(view.from)} to ${formatDate(view.to)}`}
        sub={`Printed ${printedAt.toLocaleString('en-GB')} by ${printedBy}`}
      />

      <table>
        <thead>
          <tr>
            <th>Date</th>
            <th className="num">Labour</th>
            <th className="num">Diesel</th>
            <th className="num">Misc</th>
            <th className="num">Cost</th>
            <th className="num">Cash out</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {view.days.length === 0 && (
            <tr><td colSpan={7}>Nothing recorded in this period.</td></tr>
          )}
          {view.days.map((day) => (
            <tr key={day.work_date}>
              <td>{formatDate(day.work_date)}</td>
              <td className="num">{formatRs(day.labour_rs)}</td>
              <td className="num">{formatRs(day.diesel_rs)}</td>
              <td className="num">{formatRs(day.misc_rs)}</td>
              <td className="num">{formatRs(day.cost_rs)}</td>
              <td className="num">{formatRs(day.cash_out_rs)}</td>
              <td>{day.closed ? 'Approved' : 'Unapproved'}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <th>Total</th>
            <th className="num">{formatRs(view.totals.labourRs)}</th>
            <th className="num">{formatRs(view.totals.dieselRs)}</th>
            <th className="num">{formatRs(view.totals.miscRs)}</th>
            <th className="num">{formatRs(view.totals.costRs)}</th>
            <th className="num">{formatRs(view.totals.cashOutRs)}</th>
            <th />
          </tr>
        </tfoot>
      </table>

      <h4 style={{ marginTop: 14 }}>Cost by plant</h4>
      <PlantTable view={view} />

      {/* Spelling out the difference, because the two totals get confused. */}
      <div className="totals">
        <div className="totline">
          <span>Labour earned in the period (paid weekly, not day cash)</span>
          <span>{formatRs(view.totals.labourRs)}</span>
        </div>
        <div className="totline grand">
          <span>Total cost for the period</span>
          <span>{formatRs(view.totals.costRs)}</span>
        </div>
        <div className="cashbox">
          <span>Cash paid out (diesel + misc + advances)</span>
          <span>{formatRs(view.totals.cashOutRs)}</span>
        </div>
      </div>
    </>
  );
}

export function PlantCostReport({ view, printedBy, printedAt }: Props) {
  return (
    <>
      <DocHeader
        doc="Plant cost report"
        title={`${formatDate(view.from)} to ${formatDate(view.to)}`}
        sub={`Printed ${printedAt.toLocaleString('en-GB')} by ${printedBy}`}
      />
      <PlantTable view={view} />
      <div style={{ marginTop: 10, fontSize: 12 }}>
        Each machine carries its own diesel plus every misc item booked to it — tyre
        repairs, spare parts, servicing and vehicle fuel.
      </div>
    </>
  );
}

function PlantTable({ view }: { view: SummaryView }) {
  const totals = view.plants.reduce(
    (acc, plant) => ({
      litres: acc.litres + plant.litres_x100,
      diesel: acc.diesel + plant.diesel_rs,
      misc: acc.misc + plant.misc_rs,
      total: acc.total + plant.total_rs,
    }),
    { litres: 0, diesel: 0, misc: 0, total: 0 },
  );

  return (
    <table>
      <thead>
        <tr>
          <th>Plant</th>
          <th>Code</th>
          <th className="num">Litres</th>
          <th className="num">Diesel</th>
          <th className="num">Other costs</th>
          <th className="num">Total</th>
          <th className="num">Share</th>
        </tr>
      </thead>
      <tbody>
        {view.plants.length === 0 && (
          <tr><td colSpan={7}>Nothing booked to a plant in this period.</td></tr>
        )}
        {view.plants.map((plant) => (
          <tr key={plant.plant_id}>
            <td>{plant.name}</td>
            <td>{plant.code}</td>
            <td className="num">{formatLitres(plant.litres_x100)}</td>
            <td className="num">{formatRs(plant.diesel_rs)}</td>
            <td className="num">{plant.misc_rs ? formatRs(plant.misc_rs) : '—'}</td>
            <td className="num">{formatRs(plant.total_rs)}</td>
            <td className="num">
              {totals.total ? `${Math.round((plant.total_rs / totals.total) * 100)}%` : '—'}
            </td>
          </tr>
        ))}
      </tbody>
      <tfoot>
        <tr>
          <th colSpan={2}>Total</th>
          <th className="num">{formatLitres(totals.litres)}</th>
          <th className="num">{formatRs(totals.diesel)}</th>
          <th className="num">{formatRs(totals.misc)}</th>
          <th className="num">{formatRs(totals.total)}</th>
          <th />
        </tr>
      </tfoot>
    </table>
  );
}
